import {useCallback, useEffect, useState} from 'react';

import {tierService} from '@/services/tierService';
import type {AfriexAdminTransaction} from '@/types';
import type {AfriexQueryParams} from '@/types/AfriexQueryParams';

const useTierTransactionList = (tierId: string) => {
  const [tierTransactionList, setTierTransactionList] = useState<
    Partial<AfriexAdminTransaction>[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [itemTotal, setItemTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [error, setError] = useState<unknown>(null);

  const fetchTierTransactionList = useCallback(
    async (params: AfriexQueryParams) => {
      try {
        setError(null);
        setIsLoading(true);
        const {data, total} = await tierService.getTierTransactionList(
          tierId,
          params,
        );
        setTierTransactionList(data);
        setItemTotal(total || 0);
      } catch (e) {
        setError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [tierId],
  );

  useEffect(() => {
    // `fetchTierTransactionList` try/catches into `setError`, so this only
    // fires if that catch block itself throws. Routed to the same state the
    // fetcher uses, so the screen's existing error surface still reflects it.
    fetchTierTransactionList({}).catch((e: unknown) => setError(e));
  }, [fetchTierTransactionList]);

  return {
    error,
    fetchList: fetchTierTransactionList,
    isLoading,
    page,
    setPage,
    total: itemTotal,
    list: tierTransactionList,
  };
};

export default useTierTransactionList;
